import styled from "@emotion/styled";
import {
  AppBar,
  Box,
  ButtonGroup,
  Collapse,
  DialogContent,
  Fade,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Dialog as MuiDialog,
  Paper as MuiPaper,
  Typography as MuiTypography,
  Skeleton,
  Toolbar,
  Typography
} from "@mui/material";
import Divider from "@mui/material/Divider";
import { AxiosResponse } from "axios";
import { JobDescriptionContainer } from "klayowebshared";
import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/dialogs/Dialog";
import { Snackbar } from "../../components/Snackbar";
import { useAppMutation } from "../../hooks/useAppMutation";
import { useAppQuery } from "../../hooks/useAppQuery";
import {
  IJobDetailCompentency,
  IJobDetails,
  IJobResponse,
  ISuccessImportJob,
  ITaskCompetencyJobDetail,
  ITaskJobDetail
} from "../../interfaces/Job";
import { useLocale } from "../../providers/LocaleProvider";
import { useSnackbar } from "../../providers/SnackbarProvider";
import AddIcons from "../../resources/images/icons-add.svg";
import CheckIcons from "../../resources/images/icons-check.svg";
import ChevronDownWhiteIcon from "../../resources/images/icons-chevron-down-white.svg";
import CloseIcon from "../../resources/images/icons-close.svg";
import RemoveIcons from "../../resources/images/icons-remove.svg";
import { KLAYO_COLORS } from "../../themes/colors";
import { JobDescriptionDialog } from "../profile/JobDescriptionDialog";
import ImportCompetenciesDialog from "./ImportCompetenciesDialog";
import ImportJobConfirmDialog from "./ImportJobConfirmDialog";
import ImportJobCopyDialog from "./ImportJobCopyDialog";
import ImportJobErrorDialog from "./ImportJobErrorDialog";
import ImportTaskDetailDialog from "./ImportTaskDetailDialog";
import LimitedOverlay from "./LimitedOverlay";

const SettingStandardsLibraryJobDetail = (props: any) => {
  const navigate = useNavigate();
  const { translate } = useLocale();
  const { isTrial } = props;

  const {
    open = false,
    onClose = () => {},
    jobId,
    theme,
    onCloseAllDialog = () => {},
    isImport = true,
    onImportCallback,
    // Optional analytics hooks — only the Job Builder (KI) flow passes these.
    onToggleTask,
    onToggleCompetency,
    onCancel
  } = props;
  const { showSnackbar } = useSnackbar();

  const { data: jobData, isFetching: isLoadingJobs } = useAppQuery<IJobResponse>(
    `/StandardLibrary/jobs/${jobId}`,
    {
      gcTime: 0
    }
  );

  const [importDialog, setImportDialog] = useState<any>(null);
  const [showTasks, setShowTasks] = useState(false);
  const [showCompetencies, setShowCompetencies] = useState(false);
  const [showTraining, setShowTraining] = useState(false);
  const [hideDescription, setHideDescription] = useState(false);
  const [taskSelected, setTaskSelected] = useState<Record<string, ITaskJobDetail>>({});
  const [competenciesSelected, setCompetenciesSelected] = useState<
    Record<string, IJobDetailCompentency>
  >({});
  const [showImportTaskDetail, setShowImportTaskDetail] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showImportCompetencies, setShowImportCompetencies] = useState(false);
  const [selectedCompetency, setSelectedCompetency] = useState<string | null>(null);
  const competenciesListOfOwnerRef = useRef<Record<string, string[]>>({});
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showComfirmImport, setShowComfirmImport] = useState({ isOpen: false, label: "Import" });
  const [showImportJobCopy, setShowImportJobCopy] = useState({ isOpen: false, state: "" });
  const [warningDialog, setWarningDialog] = useState<{
    open: boolean;
    competency: IJobDetailCompentency | null;
    taskIds: string[];
  }>({
    open: false,
    competency: null,
    taskIds: []
  });

  const [showNotImportedCompetencies, setShowNotImportedCompetencies] = useState(false);
  const [cacheRows, setCacheRows] = useState<Pick<ISuccessImportJob, "failedToImportCompetencies">>(
    {
      failedToImportCompetencies: []
    }
  );
  const [showImportJobError, setShowImportJobError] = useState(false);

  const importJobMutation = useAppMutation<
    AxiosResponse<ISuccessImportJob>,
    any,
    {
      jobDefinitionId: string;
      taskIds: string[];
      competencyIds: string[];
      isOverriding?: boolean;
    }
  >("post", "/StandardLibrary/jobs/import", {
    onSuccess: (response: AxiosResponse<ISuccessImportJob>, variables) => {
      if (response?.data?.isSuccess) {
        showSnackbar(translate("settings.successfullyImportedJob"), "success");
        setShowComfirmImport({ isOpen: false, label: "" });
        handleClose();
        // The import-as-copy (override) flow fires onImportCallback from its own
        // call-site onSuccess, so skip it here to avoid a duplicate.
        if (!variables?.isOverriding) onImportCallback?.();
      } else {
        // showSnackbar(response.data.errorMessage || "Failed to import job", "error");
        setCacheRows({
          failedToImportCompetencies: response.data.failedToImportCompetencies || []
        });
        setShowComfirmImport({ isOpen: false, label: "" });
        setShowNotImportedCompetencies(true);
      }
    },
    onError: (error) => {
      if (error.status === 409) {
        setShowComfirmImport({ isOpen: false, label: "" });
        setShowImportJobCopy({ isOpen: true, state: showImportJobCopy.state });
      } else {
        showSnackbar(
          error.response.data.message || translate("settings.failedToImportJob"),
          "error"
        );
      }
    }
  });

  const onCloseImportTaskDetail = useCallback(() => {
    setShowImportTaskDetail(false);
  }, []);

  const onClickSelecteTasks = useCallback((id: string, task: ITaskJobDetail) => {
    setShowImportTaskDetail(true);
    setSelectedTaskId(id);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleCloseMenu = useCallback(() => {
    setImportDialog(null);
  }, []);

  const onClickTasks = useCallback((e: React.MouseEvent, task: ITaskJobDetail) => {
    e.stopPropagation();
    onToggleTask?.(task.name);
    setTaskSelected((prev) => {
      const updated = { ...prev };
      if (updated[task.id]) {
        onUpdateCompetencyDependOnTask(task);
        delete updated[task.id];
      } else {
        onAddCompetencyDependOnTask(task);
        updated[task.id] = task;
      }
      return updated;
    });
  }, []);

  const onAddCompetencyDependOnTask = (task: ITaskJobDetail) => {
    const competencies = task.taskCompetencies;
    if (!competencies || competencies.length == 0) return;

    const competenciesListOfOwner = competenciesListOfOwnerRef.current;
    const updatedCompetencies: Record<string, IJobDetailCompentency> = {};

    competencies.forEach((competency) => {
      if (!competenciesListOfOwner[competency.id]) {
        competenciesListOfOwner[competency.id] = [];
      }

      competenciesListOfOwner[competency.id].push(task.id);

      updatedCompetencies[competency.id] = {
        id: competency.id,
        standardLibraryJobDefinitionId: jobId,
        standardLibraryCompetencyId: competency.id,
        standardLibraryCompetencyName: competency.name,
        required: false,
        mandatory: false,
        createdDate: "",
        lastUpdatedDate: ""
      };
    });

    setCompetenciesSelected((prev) => ({
      ...prev,
      ...updatedCompetencies
    }));

    competenciesListOfOwnerRef.current = competenciesListOfOwner;
  };

  const onUpdateCompetencyDependOnTask = (task: ITaskJobDetail) => {
    const competencies = task.taskCompetencies;

    if (!competencies || competencies.length == 0) return;

    const competenciesListOfOwner = competenciesListOfOwnerRef.current;
    competencies.forEach((competency) => {
      if (competenciesListOfOwner[competency.id]) {
        const taskIds = competenciesListOfOwner[competency.id];
        if (taskIds.length > 0 && taskIds.includes(task.id)) {
          if (taskIds.length === 1) {
            setCompetenciesSelected((prev) => {
              const updated = { ...prev };
              delete updated[competency.id];
              return updated;
            });
          }

          taskIds.splice(taskIds.indexOf(task.id), 1);
          const updatedCompetenciesListOfOwner = { ...competenciesListOfOwner };
          updatedCompetenciesListOfOwner[competency.id] = taskIds;
          competenciesListOfOwnerRef.current = updatedCompetenciesListOfOwner;
        }
      }
    });
  };

  const checkIfCompetencyIsRequiredByTasks = useCallback(
    (competencyId: string): boolean => {
      const competenciesListOfOwner = competenciesListOfOwnerRef.current;
      return (
        competenciesListOfOwner[competencyId] && competenciesListOfOwner[competencyId].length > 0
      );
    },
    [taskSelected]
  );

  const onClickCompetencies = useCallback(
    (e: React.MouseEvent, competency: IJobDetailCompentency) => {
      e.stopPropagation();
      onToggleCompetency?.(competency.standardLibraryCompetencyName);
      setCompetenciesSelected((prev) => {
        const updated = { ...prev };
        if (updated[competency.standardLibraryCompetencyId]) {
          if (checkIfCompetencyIsRequiredByTasks(competency.standardLibraryCompetencyId)) {
            showDialogWarning(competency);
            return prev;
          } else {
            delete updated[competency.standardLibraryCompetencyId];
          }
        } else {
          updated[competency.standardLibraryCompetencyId] = competency;
        }
        return updated;
      });
    },
    []
  );

  const showDialogWarning = useCallback((competency: IJobDetailCompentency) => {
    const competenciesListOfOwner = competenciesListOfOwnerRef.current;
    const taskIds = competenciesListOfOwner[competency.standardLibraryCompetencyId] || [];
    setWarningDialog({
      open: true,
      competency,
      taskIds
    });
  }, []);

  const closeWarningDialog = useCallback(() => {
    setWarningDialog({
      open: false,
      competency: null,
      taskIds: []
    });
  }, []);

  const onClickDetailCompetency = useCallback(
    (e: React.MouseEvent, competency: IJobDetailCompentency) => {
      e.stopPropagation();
      setSelectedCompetency(competency.standardLibraryCompetencyId);
      setShowImportCompetencies(true);
    },
    []
  );

  const onCloseImportCompetencies = useCallback(() => {
    setShowImportCompetencies(false);
  }, []);

  const toggleShowTasks = useCallback(() => setShowTasks((prev) => !prev), []);
  const toggleShowCompetencies = useCallback(() => setShowCompetencies((prev) => !prev), []);
  const toggleShowTraining = useCallback(() => setShowTraining((prev) => !prev), []);
  const toggleHideDescription = useCallback(() => setHideDescription((prev) => !prev), []);

  const handleOpenImportDialog = (event: React.MouseEvent) => {
    setImportDialog(event.currentTarget);
  };

  const data = useMemo(() => {
    if (isLoadingJobs) {
      return null;
    }

    return jobData?.jobDefinition as IJobDetails;
  }, [isLoadingJobs, jobData]);

  const { extraTasksCount, extraCompetenciesCount, renderCompetencies, renderTasks } = useMemo(
    () => ({
      extraTasksCount: data?.tasks.filter((t) => t.isExtra).length || 0,
      extraCompetenciesCount: data?.competencies.filter((c) => c.isExtra).length || 0,
      renderCompetencies: data?.competencies || [],
      renderTasks: data?.tasks || []
    }),
    [data]
  );

  const handleImportJobAndEditConfirm = useCallback(() => {
    setImportDialog(null);
    importJobMutation.mutate(
      {
        jobDefinitionId: jobId,
        taskIds: Object.keys(taskSelected),
        competencyIds: Object.keys(competenciesSelected)
      },
      {
        onSuccess: (data: AxiosResponse<ISuccessImportJob>) => {
          if (data?.data?.isSuccess) {
            showSnackbar(translate("settings.successfullyImportedJob"), "success");
            navigate(`/settings/jobs/edit/${data?.data?.jobDefinition?.jobDefinitionId}`);
            setShowComfirmImport({ isOpen: false, label: "" });
            onCloseAllDialog();
          } else {
            showSnackbar(
              data.data.errorMessage || translate("settings.failedToImportJob"),
              "error"
            );
          }
        }
      }
    );
  }, [jobId, taskSelected, competenciesSelected, navigate, showSnackbar]);

  const handleImportJobAndEdit = useCallback(() => {
    setImportDialog(null);
    setShowComfirmImport({ isOpen: true, label: "Import and Edit" });
    setShowImportJobCopy({ isOpen: false, state: "IMPORT_AND_EDIT" });
  }, []);

  const handleImportJob = () => {
    setImportDialog(null);
    setShowComfirmImport({ isOpen: true, label: "Import" });
  };

  const handleConfirmImportJob = useCallback(() => {
    const selectedTaskIds = Object.keys(taskSelected);
    const selectedCompetencyIds = Object.keys(competenciesSelected);
    importJobMutation.mutate({
      jobDefinitionId: jobId,
      taskIds: selectedTaskIds,
      competencyIds: selectedCompetencyIds
    });
  }, [jobId, taskSelected, competenciesSelected, importJobMutation]);

  const handleImportJobCopy = () => {
    const selectedTaskIds = Object.keys(taskSelected);
    const selectedCompetencyIds = Object.keys(competenciesSelected);

    importJobMutation.mutate(
      {
        jobDefinitionId: jobId,
        taskIds: selectedTaskIds,
        competencyIds: selectedCompetencyIds,
        isOverriding: true
      },
      {
        onSuccess: (response: AxiosResponse<ISuccessImportJob>) => {
          if (response?.data?.isSuccess) {
            if (showImportJobCopy.state === "IMPORT_AND_EDIT") {
              navigate(`/settings/jobs/edit/${response?.data?.jobDefinition?.jobDefinitionId}`);
              setShowImportJobCopy({ isOpen: false, state: "" });
              onCloseAllDialog();
            } else {
              showSnackbar(translate("settings.successfullyImportedJobAsCopy"), "success");
              setShowImportJobCopy({ isOpen: false, state: "" });
              handleClose();
              onImportCallback?.();
            }
          } else {
            showSnackbar(
              response?.data?.errorMessage || translate("settings.failedToImportJob"),
              "error"
            );
          }
        }
      }
    );
  };

  const handleCloseConfirmImport = useCallback(() => {
    setShowComfirmImport({ isOpen: false, label: "" });
  }, []);

  const handleCloseImportJobCopy = useCallback(() => {
    setShowImportJobCopy({ isOpen: false, state: "" });
  }, []);

  React.useEffect(() => {
    const jobDefinition = jobData?.jobDefinition;
    if (!jobDefinition) return;

    const competenciesListOfOwner: Record<string, string[]> = {};

    const competenciesFromTasks: ITaskCompetencyJobDetail[] = jobDefinition.tasks.flatMap(
      (task, index, array) => {
        const currrentTask = array[index];
        const competencies = currrentTask.taskCompetencies;

        if (task.isExtra) return [];

        competencies.forEach((competency) => {
          if (!competenciesListOfOwner[competency.id]) {
            competenciesListOfOwner[competency.id] = [];
          }
          competenciesListOfOwner[competency.id].push(currrentTask.id);
          competency.isExtra = task.isExtra;
        });

        return task.taskCompetencies;
      }
    );

    const newTaskSelected = jobDefinition.tasks.reduce<Record<string, ITaskJobDetail>>(
      (acc, task) => {
        if (!task.isExtra) {
          acc[task.id] = task;
        }
        return acc;
      },
      {}
    );

    const newCompetenciesSelected = jobDefinition.competencies.reduce<
      Record<string, IJobDetailCompentency>
    >((acc, competency) => {
      if (!competency.isExtra) {
        acc[competency.standardLibraryCompetencyId] = competency;
      }
      return acc;
    }, {});

    competenciesFromTasks.forEach((competency) => {
      newCompetenciesSelected[competency.id] = {
        id: competency.id,
        standardLibraryJobDefinitionId: jobDefinition.id,
        standardLibraryCompetencyId: competency.id,
        standardLibraryCompetencyName: competency.name,
        required: false,
        mandatory: false,
        createdDate: "",
        lastUpdatedDate: "",
        isExtra: competency.isExtra
      };
    });

    competenciesListOfOwnerRef.current = competenciesListOfOwner;
    setTaskSelected(newTaskSelected);
    setCompetenciesSelected(newCompetenciesSelected);
  }, [jobData]);

  const renderDomainAndCategoryActionMenu = useMemo(() => {
    return (
      <div>
        <MenuItem onClick={handleImportJobAndEdit}>
          <ListItemText>{translate("settings.importJobAndEdit")}</ListItemText>
        </MenuItem>
      </div>
    );
  }, [handleImportJobAndEdit, translate]);

  const errorCompetencies = useMemo(
    () => cacheRows.failedToImportCompetencies,
    [cacheRows.failedToImportCompetencies]
  );

  const onImportCompetenciesError = () => {
    setShowImportJobError(true);
    setShowNotImportedCompetencies(false);
  };

  const isShowLimitedCompetencyOverlay = useMemo(
    () => isTrial && renderCompetencies.some((c) => c.isExtra),
    [isTrial, renderCompetencies]
  );
  const isShowLimitedTaskOverlay = useMemo(
    () => isTrial && data?.tasks.some((t) => t.isExtra),
    [isTrial, data?.tasks]
  );

  return (
    <MuiDialog open={open} fullScreen>
      <StyledMenu
        open={importDialog !== null}
        anchorEl={importDialog}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        onClose={handleCloseMenu}
      >
        {renderDomainAndCategoryActionMenu}
      </StyledMenu>

      <Container>
        <DialogContent sx={{ backgroundColor: "#F8F8FE" }}>
          <ContentWrapper>
            {isLoadingJobs ? (
              <Skeleton variant='text' width={300} height={40} />
            ) : (
              <Heading variant='h4'>
                {translate("settings.jobDetails")} - {data?.name}
              </Heading>
            )}

            <Paper>
              <PaperContent>
                <SectionHeader>
                  {isLoadingJobs ? (
                    <Skeleton variant='text' width={150} height={30} />
                  ) : (
                    <SectionTitle sx={{ fontWeight: "bold" }} variant='h5'>
                      {translate("settings.jobDescription")}
                    </SectionTitle>
                  )}

                  {isLoadingJobs ? (
                    <Skeleton variant='text' width={100} height={20} />
                  ) : (
                    <PButton onClick={toggleHideDescription}>
                      {hideDescription
                        ? translate("settings.showDescription")
                        : translate("settings.hideDescription")}
                    </PButton>
                  )}
                </SectionHeader>

                <Collapse in={!hideDescription} timeout='auto' unmountOnExit>
                  {isLoadingJobs ? (
                    <Box sx={{ pt: 1 }}>
                      <Skeleton />
                      <Skeleton width='80%' />
                      <Skeleton width='60%' />
                    </Box>
                  ) : (
                    <Box
                      className='klaro_section klaro_profile-jobdesc'
                      title={translate("settings.jobDescription")}
                      sx={{
                        height: "100%",
                        boxSizing: "border-box",
                        padding: "0 !important",
                        border: "none"
                      }}
                    >
                      {data?.description && data?.description.length > 100 && (
                        <JobDescriptionContainer
                          text={data?.description || ""}
                          height={(data?.description || "").length < 100 ? 35 : 80}
                          onShowMore={() => setShowFullDescription(true)}
                          noDescriptionText={translate("common.noJobDescriptionAvailable")}
                          showAllText={translate("common.showAll")}
                        />
                      )}
                      {data?.description && data?.description.length <= 100 && (
                        <Typography
                          sx={{ fontSize: "1rem", color: KLAYO_COLORS.GreyGR900 }}
                          variant='body2'
                        >
                          <div dangerouslySetInnerHTML={{ __html: data?.description }} />
                        </Typography>
                      )}
                    </Box>
                  )}
                </Collapse>
              </PaperContent>
            </Paper>

            <Paper>
              <PaperContent>
                <SectionHeader>
                  {isLoadingJobs ? (
                    <Skeleton variant='text' width={100} height={24} />
                  ) : (
                    <SectionTitle variant='h6'>{translate("settings.jobTasks")}</SectionTitle>
                  )}

                  {isLoadingJobs ? (
                    <Skeleton variant='text' width={80} height={20} />
                  ) : (
                    <PButton onClick={toggleShowTasks}>
                      {showTasks
                        ? translate("settings.hideTasks")
                        : translate("settings.showTasks")}
                    </PButton>
                  )}
                </SectionHeader>

                {isLoadingJobs ? (
                  <Skeleton variant='text' width={150} height={20} sx={{ mt: 1 }} />
                ) : (
                  <SectionDescription
                    variant='body2'
                    sx={{ margin: "0 !important", paddingBottom: ".5rem" }}
                  >
                    {translate("settings.tasksSelected", {
                      selected: data?.tasks ? Object.keys(taskSelected).length : 0,
                      total: data?.totalTasks || 0,
                      taskWord:
                        Object.keys(taskSelected).length === 1
                          ? translate("entities.task")
                          : translate("entities.tasks")
                    })}
                  </SectionDescription>
                )}

                <Collapse in={showTasks} timeout='auto'>
                  {isLoadingJobs ? (
                    <Box sx={{ pt: 1 }}>
                      <Skeleton />
                      <Skeleton width='80%' />
                      <Skeleton width='60%' />
                    </Box>
                  ) : (
                    <div
                      className='task-container'
                      style={{
                        position: "relative"
                      }}
                    >
                      {renderTasks?.map((task, index) => (
                        <TaskItem
                          key={task.id}
                          task={task}
                          index={index}
                          totalTasks={renderTasks.length}
                          isSelected={!!taskSelected[task.id]}
                          onClickTask={onClickTasks}
                          onClickDetail={onClickSelecteTasks}
                          showAction={!task.isExtra}
                          isTrial={!!task.isExtra}
                        />
                      ))}
                      {isShowLimitedTaskOverlay && (
                        <LimitedOverlay
                          isCreatePortal={false}
                          height={48 * extraTasksCount}
                          translate={translate}
                        />
                      )}
                    </div>
                  )}
                </Collapse>
              </PaperContent>
            </Paper>

            <Paper>
              <PaperContent>
                <SectionHeader>
                  {isLoadingJobs ? (
                    <Skeleton variant='text' width={150} height={24} />
                  ) : (
                    <SectionTitle variant='h6'>
                      {translate("settings.jobCompetencies")}
                    </SectionTitle>
                  )}
                  {isLoadingJobs ? (
                    <Skeleton variant='text' width={120} height={20} />
                  ) : (
                    <PButton onClick={toggleShowCompetencies}>
                      {showCompetencies
                        ? translate("settings.hideCompetencies")
                        : translate("settings.showCompetencies")}
                    </PButton>
                  )}
                </SectionHeader>

                {isLoadingJobs ? (
                  <Skeleton variant='text' width={180} height={20} sx={{ mt: 1 }} />
                ) : (
                  <SectionDescription
                    variant='body2'
                    sx={{ margin: "0 !important", paddingBottom: ".5rem" }}
                  >
                    {translate("settings.competenciesSelected", {
                      selected: Object.keys(competenciesSelected).length,
                      total: data?.totalCompetencies || 0,
                      competencyWord:
                        Object.keys(competenciesSelected).length === 1
                          ? translate("entities.attribute")
                          : translate("entities.attributes")
                    })}
                  </SectionDescription>
                )}

                <Collapse in={showCompetencies} timeout='auto'>
                  {isLoadingJobs ? (
                    <Box sx={{ pt: 1 }}>
                      <Skeleton />
                      <Skeleton width='80%' />
                      <Skeleton width='60%' />
                    </Box>
                  ) : (
                    <div
                      className='competency-container'
                      style={{
                        position: "relative"
                      }}
                    >
                      {renderCompetencies.map((competency, index) => (
                        <CompetencyItem
                          key={competency.standardLibraryCompetencyId}
                          competency={competency}
                          index={index}
                          totalItems={renderCompetencies.length}
                          isSelected={
                            !!competenciesSelected[competency.standardLibraryCompetencyId]
                          }
                          onClickCompetency={onClickCompetencies}
                          onClickDetail={onClickDetailCompetency}
                          showAction={!competency.isExtra}
                          isTrial={!!competency.isExtra}
                        />
                      ))}
                      {isShowLimitedCompetencyOverlay && (
                        <LimitedOverlay
                          isCreatePortal={false}
                          height={48 * extraCompetenciesCount}
                          translate={translate}
                        />
                      )}
                    </div>
                  )}
                </Collapse>
              </PaperContent>
            </Paper>

            <Paper>
              <PaperContent>
                <SectionHeader>
                  {isLoadingJobs ? (
                    <Skeleton variant='text' width={130} height={24} />
                  ) : (
                    <SectionTitle variant='h6'>
                      {translate("settings.availableTraining")}
                    </SectionTitle>
                  )}
                  {isLoadingJobs ? (
                    <Skeleton variant='text' width={100} height={20} />
                  ) : (
                    <PButton onClick={toggleShowTraining}>
                      {showTraining
                        ? translate("settings.hideTraining")
                        : translate("settings.showTraining")}
                    </PButton>
                  )}
                </SectionHeader>
                {isLoadingJobs ? (
                  <Skeleton variant='text' width={200} height={20} sx={{ mt: 1 }} />
                ) : (
                  <SectionDescription variant='body2' sx={{ marginBottom: "0.75rem" }}>
                    {data?.trainings?.length
                      ? translate(
                          data.trainings.length === 1
                            ? "settings.trainingMappedSingle"
                            : "settings.trainingMappedMultiple",
                          { count: data.trainings.length }
                        )
                      : translate("settings.noTrainingMapped")}
                  </SectionDescription>
                )}
                <Collapse in={showTraining} timeout='auto'>
                  {isLoadingJobs ? (
                    <Box sx={{ pt: 1 }}>
                      <Skeleton />
                      <Skeleton width='80%' />
                      <Skeleton width='60%' />
                    </Box>
                  ) : (
                    <div>
                      {data?.trainings?.length ? (
                        data.trainings.map((training, index) => (
                          <TrainingItem
                            key={index}
                            training={training}
                            index={index}
                            totalItems={data.trainings.length}
                          />
                        ))
                      ) : (
                        <Box sx={{ padding: "1rem", minHeight: "50px" }} />
                      )}
                    </div>
                  )}
                </Collapse>
              </PaperContent>
            </Paper>
          </ContentWrapper>
        </DialogContent>

        <BottomAppBar className='klaro_employeeprofile-appbar full-width-model' elevation={0}>
          <Divider />
          <ToolbarStyled className='toolbarStyled`>'>
            <ButtonGroup>
              <Button
                label={translate("common.cancel")}
                variant='outlined'
                size='md'
                startIcon={<CloseIcon />}
                onClick={() => {
                  onCancel?.();
                  handleClose();
                }}
              />
            </ButtonGroup>
            <Box sx={{ flexGrow: 1 }}></Box>
            {isImport && (
              <ButtonGroup
                style={{
                  opacity: isLoadingJobs ? 0.5 : 1,
                  pointerEvents: isLoadingJobs ? "none" : "auto"
                }}
              >
                <Button
                  size='md'
                  theme={theme}
                  variant='filled'
                  label={translate("settings.importJobButton")}
                  className='klaro_regulations-button-add-regulation'
                  onClick={handleImportJob}
                />
                <div className='klaro_regulations-vertical-line'></div>
                <div className='klaro_regulations-domain-category'>
                  <ChevronDownWhiteIcon onClick={handleOpenImportDialog} />
                </div>
              </ButtonGroup>
            )}
          </ToolbarStyled>
        </BottomAppBar>
      </Container>

      <Fade in={showImportTaskDetail} unmountOnExit>
        <div>
          <ImportTaskDetailDialog
            theme={theme}
            taskId={selectedTaskId}
            onClose={onCloseImportTaskDetail}
            open={showImportTaskDetail}
            isImport={false}
          />
        </div>
      </Fade>

      <Fade in={showImportCompetencies} timeout={300} unmountOnExit>
        <div>
          <ImportCompetenciesDialog
            theme={theme}
            competencyId={selectedCompetency}
            onClose={onCloseImportCompetencies}
            open={showImportCompetencies}
            isImport={false}
          />
        </div>
      </Fade>

      <CompetencyWarningDialog
        open={warningDialog.open}
        competency={warningDialog.competency}
        taskIds={warningDialog.taskIds}
        tasks={renderTasks}
        onClose={closeWarningDialog}
        translate={translate}
      />

      {showFullDescription && (
        <JobDescriptionDialog
          theme={theme}
          description={data?.description || ""}
          onClose={() => setShowFullDescription(false)}
        />
      )}

      <ImportJobConfirmDialog
        open={showComfirmImport.isOpen}
        loading={importJobMutation.isPending}
        onConfirm={handleConfirmImportJob}
        onClose={handleCloseConfirmImport}
        theme={theme}
        onImportAndEdit={handleImportJobAndEditConfirm}
        label={showComfirmImport.label as "Import" | "Import and Edit"}
      />

      <ImportJobCopyDialog
        open={showImportJobCopy.isOpen}
        loading={importJobMutation.isPending}
        onConfirm={handleImportJobCopy}
        onClose={handleCloseImportJobCopy}
        theme={theme}
      />

      <Fade in={showNotImportedCompetencies} timeout={300} unmountOnExit>
        <div>
          <Snackbar
            open={true}
            duration={3000}
            type={errorCompetencies.length > 0 ? "error" : "success"}
            onClose={() => setShowNotImportedCompetencies(false)}
            message={
              <React.Fragment>
                <Typography hidden={errorCompetencies.length === 0}>
                  {translate("settings.tasksNotImported", {
                    count: errorCompetencies.length,
                    taskWord:
                      errorCompetencies.length <= 1
                        ? translate("entities.task")
                        : translate("entities.tasks")
                  })}
                </Typography>
              </React.Fragment>
            }
            action={
              <React.Fragment>
                <div
                  style={{
                    marginLeft: "auto",
                    marginRight: "0.5rem",
                    cursor: "pointer",
                    fontWeight: "bold"
                  }}
                  hidden={errorCompetencies.length === 0}
                  className='klayo-snackbar_text'
                  onClick={onImportCompetenciesError}
                >
                  {translate("common.viewDetails")}
                </div>
                <BlackIconContainer
                  style={{
                    padding: "0",
                    cursor: "pointer",
                    fontWeight: "bold"
                  }}
                  onClick={() => {
                    // dispatch({ type: 'SET_MESSAGE', payload: false });
                    // Instead of just hiding message, clear selection and potentially show errors
                    setShowNotImportedCompetencies(false);
                  }}
                  className='klayo-snackbar_text '
                >
                  <RemoveIcons />
                </BlackIconContainer>
              </React.Fragment>
            }
          />
        </div>
      </Fade>

      <ImportJobErrorDialog
        theme={theme}
        open={showImportJobError}
        onClose={() => setShowImportJobError(false)}
        competencies={errorCompetencies}
      />
    </MuiDialog>
  );
};

export default SettingStandardsLibraryJobDetail;

const StyledMenu = styled(Menu)`
  .MuiMenu-paper {
    margin-bottom: 10px !important;
  }
`;

const ToolbarStyled = styled(Toolbar)`
  @media (min-width: 1299px) {
    padding: 0 13rem;
  }

  .klaro_regulations-domain-category {
    background-color: #1b3df2;
    border-top-left-radius: 0% !important;
    border-bottom-left-radius: 0% !important;
    cursor: pointer;
  }
`;
const IconButtonStyled = styled(IconButton)`
  width: 2rem;
  height: 2rem;

  svg.success path[fill="#111827"] {
    fill: #4caf50;
  }

  svg.add path[fill="#111827"] {
    fill: #4d5ef7;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f9fafb;
`;

const Content = styled(DialogContent)`
  flex-grow: 1;
  padding: 0;
  overflow: auto;
`;

const ContentWrapper = styled.div`
  max-width: 72rem;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 699px) {
    padding: 2rem;
  }
`;

const Heading = styled(MuiTypography)`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const Paper = styled(MuiPaper)`
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  overflow: hidden;
  border-radius: 0.875rem;
  border: 2px solid rgba(230, 230, 230, 0.83);
`;

const PaperContent = styled.div`
  padding: 1.5rem 1rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled(MuiTypography)`
  font-size: 1.025rem;
  font-weight: 600;
`;

const SectionDescription = styled(MuiTypography)`
  color: ${KLAYO_COLORS.GreyGR500};
  margin-top: 0.5rem;
`;

const DescriptionText = styled(MuiTypography)``;

const BottomAppBar = styled(AppBar)`
  bottom: 0;
  top: unset;
`;

const PButton = styled.p`
  color: #4d5ef7;
  cursor: pointer;
  font-weight: 600;
`;

const TaskItem = memo(
  ({
    task,
    index,
    totalTasks,
    isSelected,
    onClickTask,
    onClickDetail,
    showAction = true,
    isTrial
  }: {
    task: ITaskJobDetail;
    index: number;
    totalTasks: number;
    isSelected: boolean;
    onClickTask: (e: React.MouseEvent, task: ITaskJobDetail) => void;
    onClickDetail: (id: string, task: ITaskJobDetail) => void;
    showAction?: boolean;
    isTrial: boolean;
  }) => {
    const handleClick = useCallback(() => {
      onClickDetail(task.id, task);
    }, [task, onClickDetail]);

    return (
      <>
        <Box
          sx={{ padding: ".55rem .55rem .55rem 0", cursor: "pointer" }}
          onClick={isTrial ? () => {} : handleClick}
        >
          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            minHeight={32}
          >
            <Box
              sx={{
                fontSize: ".9rem",
                color: isTrial ? KLAYO_COLORS.GreyGR500 : "inherit"
              }}
            >
              {task.name}
            </Box>
            <Box hidden={!showAction}>
              <IconButtonStyled size='small'>
                {!isTrial && (
                  <>
                    {isSelected ? (
                      <CheckIcons className='success' onClick={(e) => onClickTask(e, task)} />
                    ) : (
                      <AddIcons className='add' onClick={(e) => onClickTask(e, task)} />
                    )}
                  </>
                )}

                {isTrial && <Box sx={{ fontSize: ".8rem", color: KLAYO_COLORS.GreyGR500 }}></Box>}
              </IconButtonStyled>
            </Box>
          </Box>
        </Box>
        {index === totalTasks - 1 ? null : <Divider />}
      </>
    );
  }
);
TaskItem.displayName = "TaskItem";

const CompetencyItem = memo(
  ({
    competency,
    index,
    totalItems,
    isSelected,
    onClickCompetency,
    onClickDetail,
    isTrial,
    showAction = true
  }: {
    competency: IJobDetailCompentency;
    index: number;
    totalItems: number;
    isSelected: boolean;
    onClickCompetency: (e: React.MouseEvent, competency: IJobDetailCompentency) => void;
    onClickDetail: (e: React.MouseEvent, competency: IJobDetailCompentency) => void;
    isTrial: boolean;
    showAction: boolean;
  }) => {
    return (
      <React.Fragment>
        <Box sx={{ padding: ".55rem .55rem .55rem 0", cursor: "pointer" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
            onClick={(e) => !isTrial && onClickDetail(e, competency)}
            minHeight={32}
          >
            <Box>{competency.standardLibraryCompetencyName}</Box>
            <Box hidden={!showAction}>
              <IconButtonStyled size='small'>
                {isSelected ? (
                  <CheckIcons
                    className='success'
                    onClick={(e) => onClickCompetency(e, competency)}
                  />
                ) : (
                  <AddIcons className='add' onClick={(e) => onClickCompetency(e, competency)} />
                )}
              </IconButtonStyled>
            </Box>
          </Box>
        </Box>
        {index === totalItems - 1 ? null : <Divider />}
      </React.Fragment>
    );
  }
);
CompetencyItem.displayName = "CompetencyItem";

const TrainingItem = memo(
  ({
    training,
    index,
    totalItems
  }: {
    training: { courseName: string };
    index: number;
    totalItems: number;
  }) => {
    return (
      <React.Fragment>
        <Box sx={{ padding: ".8rem .775rem .775rem 0" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>{training.courseName}</Box>
          </Box>
        </Box>
        {index === totalItems - 1 ? null : <Divider />}
      </React.Fragment>
    );
  }
);
TrainingItem.displayName = "TrainingItem";

const CompetencyWarningDialog = memo(
  ({
    open,
    competency,
    taskIds,
    tasks,
    onClose,
    translate
  }: {
    open: boolean;
    competency: IJobDetailCompentency | null;
    taskIds: string[];
    tasks: ITaskJobDetail[];
    onClose: () => void;
    translate: (key: string, options?: Record<string, unknown>) => string;
  }) => {
    const requiredTasks = useMemo(() => {
      if (!competency || taskIds.length === 0) return [];
      return tasks.filter((task) => taskIds.includes(task.id));
    }, [competency, taskIds, tasks]);

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='sm'
        fullWidth={true}
        title={translate("settings.cannotRemoveCompetency")}
        actions={[
          {
            label: translate("common.close"),
            onClick: onClose
          }
        ]}
      >
        <Box sx={{ mb: 2, fontSize: "90%" }}>
          <MuiTypography
            variant='body1'
            dangerouslySetInnerHTML={{
              __html: translate("settings.competencyRequiredByTasks", {
                competencyName: competency?.standardLibraryCompetencyName || ""
              })
            }}
          />
        </Box>

        {requiredTasks.map((task, index) => (
          <TaskItem
            key={task.id}
            task={task}
            index={index}
            totalTasks={requiredTasks.length}
            isSelected={true}
            onClickDetail={() => {}}
            showAction={false}
            onClickTask={() => {}}
            isTrial={false}
          />
        ))}
      </Dialog>
    );
  }
);
CompetencyWarningDialog.displayName = "CompetencyWarningDialog";

const BlackIconContainer = styled.div`
  svg {
    path[fill="#1B3DF2"] {
      fill: #000;
    }
  }
`;
